/**
 * ComponentMosaic — Mosaic family renderer.
 *
 * Images tile into a balanced collage on one side of the component,
 * with content (titles, text, actions) stacked on the other side.
 *
 * Variants:
 *   - "mosaic" / "mosaic_right": static collage (contained)
 *   - "mosaic_full" / "mosaic_right_full": full-bleed collage
 *   - "mosaic_shuffle" / "mosaic_right_shuffle": animated — images
 *     periodically swap positions in the grid (like HeroMosaic shuffle)
 *   - "mosaic_drift" / "mosaic_right_drift": animated — Ken Burns
 *     slow zoom/pan effect on each tile
 *
 * Edit mode: add buttons appear after each bucket section.
 */
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import {
  bucketElements,
  BucketRow,
  RenderItem,
  BucketAddButton,
  EmptyBucketPlaceholder,
  elementHasRenderableMedia,
  coverFrameRounding,
  COVER_WRAPPER_UNROUND,
  COVER_WRAPPER_UNROUND_KEEP_IMG,
  SortableItemWrapper,
} from "./componentModeUtils";
import { buildDriftPresets } from "./driftUtils";
import { EditContext } from "../../../editor/context/EditContext";
import { DragHandleProvider } from "../../../editor/context/DragHandleContext";
import { makeSortableId } from "../../../editor/utils/reorderCache";

const FULL_BLEED_MAX_HEIGHT = "600px";

function isFullBleed(modeId) {
  return modeId === "mosaic_full" || modeId === "mosaic_right_full";
}

function isMediaRight(modeId) {
  return (
    modeId === "mosaic_right" ||
    modeId === "mosaic_right_full" ||
    modeId === "mosaic_right_shuffle" ||
    modeId === "mosaic_right_drift"
  );
}

function isShuffle(modeId) {
  return modeId === "mosaic_shuffle" || modeId === "mosaic_right_shuffle";
}

function isDrift(modeId) {
  return modeId === "mosaic_drift" || modeId === "mosaic_right_drift";
}

function getMosaicLayout(imageCount) {
  if (imageCount <= 1) return "single";
  if (imageCount === 2) return "stacked";
  if (imageCount === 3) return "feature";
  return "grid";
}

// ---------------------------------------------------------------------------
// Shuffle hook (adapted from HeroMosaic)
// ---------------------------------------------------------------------------

const SHUFFLE_CYCLE_MS = 3200;
const SHUFFLE_OUT_MS = 500;
const SHUFFLE_IN_MS = 500;

function useMosaicShuffle(count, { paused = false } = {}) {
  const [permutation, setPermutation] = useState(() =>
    Array.from({ length: count }, (_, i) => i),
  );
  const [swap, setSwap] = useState(null);
  const nextPairRef = useRef(0);

  useEffect(() => {
    setPermutation(Array.from({ length: count }, (_, i) => i));
    setSwap(null);
    nextPairRef.current = 0;
  }, [count]);

  useEffect(() => {
    if (paused || count < 2) return;

    let cancelled = false;
    let intervalId = null;
    let outTimer = null;
    let inTimer = null;

    const tick = () => {
      if (cancelled) return;
      const a = nextPairRef.current % count;
      const b = (a + 1) % count;
      nextPairRef.current = (a + 1) % count;

      setSwap({ a, b, phase: "out" });

      outTimer = setTimeout(() => {
        if (cancelled) return;
        setPermutation((prev) => {
          const next = [...prev];
          [next[a], next[b]] = [next[b], next[a]];
          return next;
        });
        setSwap({ a, b, phase: "in" });

        inTimer = setTimeout(() => {
          if (cancelled) return;
          setSwap(null);
        }, SHUFFLE_IN_MS);
      }, SHUFFLE_OUT_MS);
    };

    const start = () => {
      if (intervalId) return;
      intervalId = setInterval(tick, SHUFFLE_CYCLE_MS);
    };
    const stop = () => {
      clearInterval(intervalId);
      intervalId = null;
      clearTimeout(outTimer);
      outTimer = null;
      clearTimeout(inTimer);
      inTimer = null;
    };

    const handleVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    if (!document.hidden) start();
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [count, paused]);

  return { permutation, swap };
}

// ---------------------------------------------------------------------------
// Drift (Ken Burns) presets — deterministic per slot index
// ---------------------------------------------------------------------------

// Ken Burns motion intents (zoom + pan) authored as scale + translate fractions.
// Every keyframe is run through `buildDriftPresets`, which clamps the translate
// so the tile ALWAYS covers its frame — even at the zoomed-out (smaller-scale)
// end of the animation. The previous hand-written presets zoomed out to ~1.05
// while translating up to 3%, which exceeds the (S-1)/(2S) coverage limit and
// let the frame edge/background show through (eng-00030). Minimum scale is kept
// comfortably above 1 so there is real overflow for the pan to move within.
const DRIFT_INTENTS = [
  { fromScale: 1.12, fromTx: 0, fromTy: 0, toScale: 1.34, toTx: -0.05, toTy: -0.04 },
  { fromScale: 1.14, fromTx: 0.03, fromTy: -0.02, toScale: 1.36, toTx: -0.04, toTy: 0.05 },
  { fromScale: 1.12, fromTx: -0.03, fromTy: 0.03, toScale: 1.32, toTx: 0.05, toTy: -0.04 },
  { fromScale: 1.15, fromTx: 0, fromTy: -0.03, toScale: 1.38, toTx: -0.05, toTy: 0.03 },
  { fromScale: 1.13, fromTx: 0.03, fromTy: 0.03, toScale: 1.35, toTx: -0.04, toTy: -0.05 },
  { fromScale: 1.12, fromTx: -0.03, fromTy: -0.03, toScale: 1.34, toTx: 0.05, toTy: 0.04 },
];

const DRIFT_PRESETS = buildDriftPresets(DRIFT_INTENTS);

const DRIFT_DURATION_MS = 9000;

// ---------------------------------------------------------------------------
// Renderer
// ---------------------------------------------------------------------------

const ComponentMosaic = ({ component, elements, modeDefinition, breakpoint }) => {
  const modeId = component?.component_mode;
  const mediaOnRight = isMediaRight(modeId);
  const fullBleed = isFullBleed(modeId);
  const shuffleMode = isShuffle(modeId);
  const driftMode = isDrift(modeId);
  const { bucketed } = bucketElements(elements, modeDefinition);
  const editContext = useContext(EditContext);
  const isEditMode = !!editContext;

  const images = bucketed.image || [];
  const titles = bucketed.title || [];
  const texts = bucketed.text || [];
  const buttonCollections = bucketed.button_collection || [];
  const buttons = bucketed.button || [];
  const files = bucketed.file || [];

  const compId = component?.id;
  const isMobile = breakpoint === "small" || breakpoint === "extra_small";
  const layout = getMosaicLayout(images.length);
  const totalElements = elements?.length || 0;

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || false;
  }, []);

  // Respect the OS "reduce motion" setting on published sites, but always play
  // animations inside the editor so creators can preview the mode they picked.
  const motionEnabled = isEditMode || !prefersReducedMotion;

  const { permutation, swap } = useMosaicShuffle(images.length, {
    paused: !shuffleMode || !motionEnabled,
  });

  // `COVER_FILL` makes the media fill the tile and crop with `object-cover`. The
  // trailing `!items-center`/`!justify-center` neutralise the per-image `position`
  // flex alignment that `Image.js` applies (`getPositionStyles(item.position)`):
  // for cover-cropped media the per-image corner is meaningless, so any residual
  // gap must keep the image centered rather than parked in its `position` corner
  // (e.g. bottom-right).
  //
  // `[&_.image-size-box]:!p-0` zeroes the image's OWN padding inside a cover tile.
  // `Image.js` applies `container_style.rounded_border` to the <img> but its
  // padding to the surrounding `.image-size-box` sizing wrapper. In a cover tile
  // the tile IS the layout-owned frame (like the reset margins), so honoring the
  // per-image padding would inset the cover <img> inside an `overflow-hidden`
  // square wrapper and crop the bitmap to straight corners. Resetting it lets the
  // cover <img> fill the tile and keep its rounded corners regardless of padding.
  const COVER_FILL = "[&_div]:!h-full [&_div]:!w-full [&_div]:!max-w-none [&_div]:!items-center [&_div]:!justify-center [&_.image-size-box]:!p-0 [&_.progressive-image-wrapper]:!h-full [&_.progressive-image-wrapper]:!w-full [&_.progressive-image-wrapper]:!max-w-none [&_img]:!w-full [&_img]:!h-full [&_img]:!object-cover [&_img]:!object-center [&_img]:!max-h-none [&_img]:!max-w-none [&_img]:!box-border [&_video]:!w-full [&_video]:!h-full [&_video]:!object-cover [&_video]:!object-center";
  const MOSAIC_ROW_HEIGHT = 220;

  // Static cover "frame" tiles (contained, full-bleed and shuffle). The tile is
  // a square `overflow-hidden` box; the cover `<img>` fills it and is centered
  // (COVER_FILL forces object-cover + descendant items/justify-center, and the
  // automatic-layout margin reset means a non-center `position`/margin can't park
  // the image in a corner). The tile imposes NO rounding of its own — only the
  // ProgressiveImage wrapper's hardcoded 8px is neutralized
  // (COVER_WRAPPER_UNROUND_KEEP_IMG) while the image KEEPS its own
  // `container_style.rounded_border`. So the visible corner radius always equals
  // the image's own (square image → square tile; rounded image → rounded tile),
  // in preview AND the editor — where the renderer only has minimal refs and
  // cannot read `container_style`, but `Image.js` still rounds the real `<img>`
  // correctly once its data loads.
  const COVER_TILE_BASE = `overflow-hidden ${COVER_FILL}`;
  const coverTileClass = (extra = "") =>
    `${COVER_TILE_BASE} ${COVER_WRAPPER_UNROUND_KEEP_IMG} ${extra}`.trim();

  // ---- Per-image reordering (drag to move within the component) ----------
  // The mosaic groups its images into a "frame" collage. Each image must be
  // individually draggable so creators can reorder the collage — exactly like
  // every other automatic mode (which reorders via `BucketRow`). Multi-image
  // mosaic layouts render media through bare `RenderItem`, so without this each
  // image's `EditableElement` would inherit the *component's* drag context: the
  // drag "dots" would move the whole component (not the image) and render as a
  // pill clipped inside the `overflow-hidden` cover tile. `renderMediaTile`
  // makes the cover tile itself the sortable node (drag-anywhere, no handle
  // pill), so dragging reorders the image and the selection chrome portals
  // above the tile. `index` is the image's position within `images`, mirroring
  // `BucketRow`'s per-bucket sortable data so the shared drag-end handler
  // (EditWebsitePage) reorders items within the component.
  const imageSortData = (img, index) => ({
    parentType: "component",
    parentId: compId,
    kind: "item",
    itemType: img.type,
    index,
    autoLayout: true,
    autoBucket: null,
  });

  const renderMediaTile = (img, index, className, style, inner) => {
    const media = inner ?? <RenderItem element={img} />;
    if (!isEditMode) {
      return (
        <div key={img.id} className={className} style={style}>
          {media}
        </div>
      );
    }
    // The tile is the per-image drag-to-reorder sortable node. Images
    // intentionally do NOT get an inter-item "+" add button — it clutters the
    // collage; new images are added via the trailing bucket "add" button. A
    // tile that is already `absolute` (full-bleed single) is its own
    // positioning context; otherwise add `relative` so the selection chrome
    // anchors to THIS tile.
    const anchorClass = /\b(absolute|fixed|relative)\b/.test(className) ? "" : "relative";
    return (
      <SortableItemWrapper
        key={img.id}
        id={makeSortableId(img)}
        data={imageSortData(img, index)}
        className={`${anchorClass} ${className}`.trim()}
        style={style}
      >
        {media}
      </SortableItemWrapper>
    );
  };

  // Wrap a layout's media in a SortableContext so the tiles inside become
  // reorderable. `rectSortingStrategy` suits the 2D collage grid. No-op in
  // published mode (no DndContext) and for the single-image layout (handled by
  // BucketRow, which provides its own SortableContext).
  const imageSortIds = images.map((img) => makeSortableId(img));
  const withImageSortContext = (node) => {
    if (!isEditMode || imageSortIds.length === 0) return node;
    return (
      <SortableContext items={imageSortIds} strategy={rectSortingStrategy}>
        {node}
      </SortableContext>
    );
  };

  // For non-reorderable image slots (empty placeholders, and the auto-animated
  // shuffle tiles), neutralize the inherited component drag context so the
  // broken/clipped drag pill never appears. The image stays selectable.
  const SuppressDrag = ({ children }) =>
    isEditMode ? (
      <DragHandleProvider value={{ dragListeners: null, dragAnywhereEnabled: false }}>
        {children}
      </DragHandleProvider>
    ) : (
      children
    );

  // Placeholders ("No Image Selected") must never be wrapped in the
  // overflow-hidden object-cover tile — that clips the dashed affordance.
  // Real media keeps the cover treatment; placeholders render naturally and
  // centered so the full add-image affordance stays visible.
  const renderContainedTile = (img, index, extraClassName = "") =>
    elementHasRenderableMedia(img) ? (
      renderMediaTile(img, index, coverTileClass(extraClassName))
    ) : (
      <div
        key={img.id}
        className={`relative flex items-center justify-center p-2 ${extraClassName}`.trim()}
      >
        <SuppressDrag>
          <RenderItem element={img} />
        </SuppressDrag>
      </div>
    );

  const renderMosaicGrid = () => {
    if (images.length === 0) return null;

    if (layout === "single") {
      const single = images[0];
      const singleHasMedia = elementHasRenderableMedia(single);
      return (
        <div
          className={`w-full ${singleHasMedia ? coverTileClass() : ""}`.trim()}
          style={singleHasMedia ? { minHeight: `${MOSAIC_ROW_HEIGHT}px` } : undefined}
        >
          <BucketRow
            elements={images}
            compId={compId}
            className={`w-full ${singleHasMedia ? "h-full" : "flex justify-center"}`}
          />
        </div>
      );
    }

    if (layout === "stacked") {
      const rows = images.length;
      return withImageSortContext(
        <div
          className="w-full grid"
          style={{
            gridTemplateRows: `repeat(${rows}, minmax(${MOSAIC_ROW_HEIGHT}px, 1fr))`,
            gap: "8px",
          }}
        >
          {images.map((img, idx) => renderContainedTile(img, idx))}
        </div>,
      );
    }

    if (layout === "feature") {
      const featured = images[0];
      const secondaryImages = images.slice(1);
      return withImageSortContext(
        <div
          className="w-full grid grid-cols-2"
          style={{ gridTemplateRows: `repeat(2, minmax(${MOSAIC_ROW_HEIGHT}px, 1fr))`, gap: "8px" }}
        >
          {renderContainedTile(featured, 0, "row-span-2")}
          {secondaryImages.map((img, i) => renderContainedTile(img, i + 1))}
        </div>,
      );
    }

    const cols = images.length <= 4 ? 2 : (isMobile ? 2 : 3);
    const rows = Math.ceil(images.length / cols);
    return withImageSortContext(
      <div
        className="w-full grid"
        style={{
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, minmax(${MOSAIC_ROW_HEIGHT}px, 1fr))`,
          gap: "8px",
        }}
      >
        {images.map((img, idx) => renderContainedTile(img, idx))}
      </div>,
    );
  };

  const renderShuffleMosaic = () => {
    if (images.length === 0) return null;

    const cols = images.length <= 2 ? images.length : images.length <= 4 ? 2 : 3;
    const rows = Math.ceil(images.length / cols);

    return (
      <div
        className="w-full grid"
        style={{
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, minmax(${MOSAIC_ROW_HEIGHT}px, 1fr))`,
          gap: "4px",
        }}
      >
        {images.map((_img, slotIndex) => {
          const photoIndex = permutation[slotIndex] ?? slotIndex;
          const img = images[photoIndex] || images[slotIndex];

          let transitionClass = "transition-all duration-500 ease-in-out";
          let transformStyle = {};
          if (swap) {
            if (slotIndex === swap.a) {
              transformStyle = swap.phase === "out"
                ? { opacity: 0, transform: "translateY(-100%)" }
                : { opacity: 1, transform: "translateY(0)" };
            } else if (slotIndex === swap.b) {
              transformStyle = swap.phase === "out"
                ? { opacity: 0, transform: "translateY(100%)" }
                : { opacity: 1, transform: "translateY(0)" };
            }
          }

          return (
            <div
              key={`mosaic-slot-${slotIndex}`}
              className={`relative ${coverTileClass(transitionClass)}`}
              style={transformStyle}
            >
              <SuppressDrag>
                <RenderItem element={img} />
              </SuppressDrag>
            </div>
          );
        })}
      </div>
    );
  };

  const renderDriftMosaic = () => {
    if (images.length === 0) return null;

    const cols = images.length <= 2 ? images.length : images.length <= 4 ? 2 : 3;
    const rows = Math.ceil(images.length / cols);

    return withImageSortContext(
      <div
        className="w-full grid"
        style={{
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, minmax(${MOSAIC_ROW_HEIGHT}px, 1fr))`,
          gap: "4px",
        }}
      >
        {images.map((img, idx) => {
          const durationSec = (DRIFT_DURATION_MS + idx * 900) / 1000;
          const delaySec = (idx * 600) / 1000;
          // The frame's rounding is the image's own configured radius
          // (container_style.rounded_border), not a hardcoded value.
          const { className: roundedClass, clip } = coverFrameRounding(img);

          // The tile is the static rounded "frame" AND the sortable node. Its
          // `clip-path` rounded inset clips the zooming (transformed/composited)
          // child to exactly the image's configured rounded border — so the
          // frame's corners stay rounded and consistent while the Ken Burns zoom
          // scales inside it. The inner wrapper's own 8px rounding is neutralized
          // so the frame is the single source of rounding. The drag transform
          // (during reorder) lands on the frame while the Ken Burns animation
          // stays on the inner wrapper, so the two transforms never conflict.
          const frameClass = `relative overflow-hidden ${roundedClass} ${COVER_FILL} ${COVER_WRAPPER_UNROUND}`;
          const frameStyle = { clipPath: clip, WebkitClipPath: clip };
          const inner = (
            <div
              className="w-full h-full"
              style={motionEnabled ? {
                animation: `mosaic-drift-${idx % DRIFT_PRESETS.length} ${durationSec}s ease-in-out ${delaySec}s infinite alternate`,
              } : {}}
            >
              <RenderItem element={img} />
            </div>
          );

          return renderMediaTile(img, idx, frameClass, frameStyle, inner);
        })}
        {motionEnabled && (
          <style>{DRIFT_PRESETS.map((preset, i) => `
            @keyframes mosaic-drift-${i} {
              from { transform: ${preset.from}; }
              to { transform: ${preset.to}; }
            }
          `).join("")}</style>
        )}
      </div>,
    );
  };

  const renderFullBleedMosaic = () => {
    if (images.length === 0) return null;

    if (layout === "single") {
      return withImageSortContext(
        renderMediaTile(images[0], 0, `absolute inset-0 ${coverTileClass()}`),
      );
    }

    const CELL_GAP = "4px";

    // Placeholders ("No Image Selected") must not be forced into the
    // overflow-hidden cover cell — that clips the affordance. Render them
    // naturally and centered; real media keeps the full-bleed cover treatment.
    const renderCell = (img, index) =>
      elementHasRenderableMedia(img) ? (
        renderMediaTile(img, index, coverTileClass())
      ) : (
        <div key={img.id} className="relative flex items-center justify-center p-2">
          <SuppressDrag>
            <RenderItem element={img} />
          </SuppressDrag>
        </div>
      );

    if (layout === "stacked") {
      return withImageSortContext(
        <div className="absolute inset-0 grid grid-rows-2" style={{ gap: CELL_GAP }}>
          {images.map((img, idx) => renderCell(img, idx))}
        </div>,
      );
    }

    if (layout === "feature") {
      const featured = images[0];
      const secondaryImages = images.slice(1);
      return withImageSortContext(
        <div className="absolute inset-0 grid grid-cols-2" style={{ gap: CELL_GAP }}>
          {renderCell(featured, 0)}
          <div className="grid grid-rows-2" style={{ gap: CELL_GAP }}>
            {secondaryImages.map((img, i) => renderCell(img, i + 1))}
          </div>
        </div>,
      );
    }

    const cols = images.length <= 4 ? 2 : 3;
    return withImageSortContext(
      <div
        className="absolute inset-0 grid"
        style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: CELL_GAP }}
      >
        {images.map((img, idx) => renderCell(img, idx))}
      </div>,
    );
  };

  // The full-bleed / drift / shuffle treatments force each image into a
  // fixed-size, overflow-hidden cover frame — which clips "No Image Selected"
  // placeholders (and the single full-bleed path doesn't even gate them). Only
  // use those clipping cover layouts when EVERY image has media; if any slot is
  // still an empty placeholder, fall back to the contained grid, which renders
  // placeholders naturally and never clips them. (`some` would let a single
  // filled slot drag unfilled placeholders into the clipping layout.)
  const imagesAllHaveMedia =
    images.length > 0 && images.every(elementHasRenderableMedia);

  const renderMediaContent = () => {
    if (shuffleMode) return imagesAllHaveMedia ? renderShuffleMosaic() : renderMosaicGrid();
    if (driftMode) return imagesAllHaveMedia ? renderDriftMosaic() : renderMosaicGrid();
    if (fullBleed && !isMobile) return imagesAllHaveMedia ? renderFullBleedMosaic() : renderMosaicGrid();
    return renderMosaicGrid();
  };

  const MosaicBlock = (
    <div
      className={fullBleed && !isMobile
        ? `w-1/2 flex-shrink-0 relative min-h-[300px] ${imagesAllHaveMedia ? "overflow-hidden" : ""}`
        : (isMobile ? "w-full" : "w-1/2 flex-shrink-0")}
      style={fullBleed && !isMobile ? { maxHeight: FULL_BLEED_MAX_HEIGHT } : undefined}
    >
      {images.length > 0 ? (
        <>
          {renderMediaContent()}
          {isEditMode && !fullBleed && (
            <BucketAddButton compId={compId} bucket="image" order={totalElements} className="-mt-4 z-10" />
          )}
        </>
      ) : isEditMode ? (
        <EmptyBucketPlaceholder compId={compId} bucket="image" order={totalElements} />
      ) : null}
    </div>
  );

  const contentItems = [...titles, ...texts, ...buttonCollections, ...buttons]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const ContentColumn = (
    <div className={isMobile ? "w-full" : "w-1/2 flex flex-col justify-center"}>
      {contentItems.length > 0 && (
        <BucketRow elements={contentItems} compId={compId} className="w-full" />
      )}

      {isEditMode && (
        <>
          {titles.length === 0 && (
            <EmptyBucketPlaceholder compId={compId} bucket="title" order={totalElements} />
          )}
          {texts.length === 0 && (
            <EmptyBucketPlaceholder compId={compId} bucket="text" order={totalElements} />
          )}
          {contentItems.length > 0 && (
            <BucketAddButton compId={compId} bucket="text" order={totalElements} />
          )}
        </>
      )}
    </div>
  );

  const FilesRow = files.length > 0 ? (
    <BucketRow elements={files} compId={compId} className="w-full" />
  ) : null;

  // Mobile-only "wrap" layout: with 4+ images, split the collage so half sit
  // ABOVE the text/title and half BELOW it (instead of the whole collage on
  // top). Renders a simple 2-column contained grid per half. `indexOffset`
  // keeps each tile's per-image sortable index aligned with its position in the
  // full `images` array, so drag-to-reorder still works across both halves.
  const renderMobileImageHalf = (imgs, indexOffset) => {
    if (imgs.length === 0) return null;
    const cols = 2;
    const rows = Math.ceil(imgs.length / cols);
    return (
      <div
        className="w-full grid"
        style={{
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, minmax(${MOSAIC_ROW_HEIGHT}px, 1fr))`,
          gap: "8px",
        }}
      >
        {imgs.map((img, i) => renderContainedTile(img, indexOffset + i))}
      </div>
    );
  };

  if (isMobile) {
    // The shuffle/drift animated collages must stay a single grouped block, so
    // only split the static-grid path (default mosaic + full-bleed on mobile,
    // which already falls back to the contained grid).
    const usesAnimatedCollage = (shuffleMode || driftMode) && imagesAllHaveMedia;
    const splitForMobile = images.length >= 4 && !usesAnimatedCollage;

    if (splitForMobile) {
      const half = Math.ceil(images.length / 2);
      const firstHalf = images.slice(0, half);
      const secondHalf = images.slice(half);
      return (
        <div className="w-full flex flex-col group/mosaic gap-4">
          {withImageSortContext(
            <>
              {renderMobileImageHalf(firstHalf, 0)}
              {ContentColumn}
              {renderMobileImageHalf(secondHalf, half)}
            </>,
          )}
          {isEditMode && (
            <BucketAddButton compId={compId} bucket="image" order={totalElements} />
          )}
          {FilesRow}
        </div>
      );
    }

    return (
      <div className="w-full flex flex-col group/mosaic">
        {MosaicBlock}
        {ContentColumn}
        {FilesRow}
      </div>
    );
  }

  return (
    <div className="w-full group/mosaic">
      <div className={`flex ${mediaOnRight ? "flex-row-reverse" : "flex-row"}`}>
        {MosaicBlock}
        {ContentColumn}
      </div>
      {FilesRow}
    </div>
  );
};

export default React.memo(ComponentMosaic);
